import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import Modal from 'components/_UI/Modal';
import Fieldset from 'components/_UI/Fieldset';
import Button from 'components/_UI/Button';
import Text from 'components/_UI/Text';
import Loader from 'components/_UI/Loader';
import CurrencyInputField from 'components/_UI/CurrencyInputField';
import Select from 'react-select';
import { createInventoryTransaction } from 'api/requests/inventory';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const Container = styled(Column)`
    width: 100%;
    padding-top: 10px;
    padding-bottom: 20px;
`;

const FormContainer = styled.form`
    text-align: left;
    margin-top: 15px;
`;

const ContentBasicFields = styled(Column)`
    width: 100%;
    margin-bottom: 10px;
    gap: 10px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: flex-end;
        gap: 15px;
    }
`;

const ContentFieldStatus = styled(Column)`
    width: 100%;
    margin-bottom: 10px;
    & > p {
        font-size: 12px;
        font-weight: 600;
        color: ${colors.nightDark} !important;
        margin-bottom: 4px;
    }
`;

const ContentButtons = styled(Column)`
    margin-top: 25px;
    gap: 10px;
    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        justify-content: flex-end;
    }
`;

const DirectionRow = styled.div`
    display: flex;
    gap: 8px;
`;

const DirectionChip = styled.button`
    flex: 1;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: 1.5px solid ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    background: ${({ $selected }) => $selected ? `${panelAccentAlpha(colors.occasionalPurple, '15')}` : 'transparent'};
    color: ${({ $selected }) => $selected ? panelAccent(colors.occasionalPurple) : colors.night};
    transition: all 0.15s;
`;

const typeOptions = [
    { value: 'purchase', label: 'Compra (entrada)' },
    { value: 'manual_adjustment', label: 'Ajuste manual' },
    { value: 'waste', label: 'Perda / Quebra' },
];

export default function InventoryTransactionModal({
    isOpen,
    onClose,
    establishmentSlug,
    item,
    onSuccess,
    toast
}) {
    const [type, setType] = useState(typeOptions[0]);
    const [direction, setDirection] = useState('in');
    const [quantity, setQuantity] = useState('');
    const [totalCost, setTotalCost] = useState('');
    const [supplier, setSupplier] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setType(typeOptions[0]);
            setDirection('in');
            setQuantity('');
            setTotalCost('');
            setSupplier('');
            setNote('');
        }
    }, [isOpen, item]);

    const handleCurrencyChange = (value) => {
        if (value) {
            return value.replace('.', '');
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const qtyNum = Number(quantity);
        if (!qtyNum || qtyNum <= 0) {
            toast.error('Informe uma quantidade válida.');
            return;
        }

        let signedQuantity;
        if (type.value === 'purchase') {
            signedQuantity = Math.abs(qtyNum);
        } else if (type.value === 'waste') {
            signedQuantity = -Math.abs(qtyNum);
        } else {
            signedQuantity = direction === 'out' ? -Math.abs(qtyNum) : Math.abs(qtyNum);
        }

        try {
            setLoading(true);
            const payload = {
                type: type.value,
                quantity: signedQuantity
            };

            if (type.value === 'purchase') {
                if (totalCost) payload.total_cost = Number(totalCost);
                if (supplier) payload.supplier = supplier;
            }
            if (note) payload.note = note;

            await createInventoryTransaction(establishmentSlug, item.id, payload);
            toast.success('Movimentação registrada com sucesso!');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error creating inventory transaction:', error);
            toast.error('Erro ao registrar movimentação. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="medium"
        >
            <Container>
                <Text name={'highlight'}>Movimentar: {item?.name}</Text>
                <Text name="small" color={colors.night} style={{ marginTop: '4px', marginBottom: '10px' }}>
                    Estoque atual: {Number(item?.current_quantity || 0).toFixed(2)} {item?.unit_of_measure}
                </Text>

                <FormContainer onSubmit={handleSubmit}>
                    <ContentFieldStatus>
                        <Text>Tipo de movimentação *</Text>
                        <Select
                            placeholder="Selecione"
                            options={typeOptions}
                            value={type}
                            onChange={setType}
                        />
                    </ContentFieldStatus>

                    <ContentBasicFields>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label={`Quantidade (${item?.unit_of_measure || ''}) *`}
                                layout="squired"
                                inputType="number"
                                placeholder="Ex: 10"
                                value={quantity}
                                onChangeValue={setQuantity}
                            />
                        </Column>
                        {type.value === 'manual_adjustment' && (
                            <Column style={{ flex: 1 }}>
                                <Text>Direção *</Text>
                                <DirectionRow>
                                    <DirectionChip
                                        type="button"
                                        $selected={direction === 'in'}
                                        onClick={() => setDirection('in')}
                                    >
                                        Entrada (+)
                                    </DirectionChip>
                                    <DirectionChip
                                        type="button"
                                        $selected={direction === 'out'}
                                        onClick={() => setDirection('out')}
                                    >
                                        Saída (−)
                                    </DirectionChip>
                                </DirectionRow>
                            </Column>
                        )}
                    </ContentBasicFields>

                    {type.value === 'purchase' && (
                        <ContentBasicFields>
                            <Column style={{ flex: 1 }}>
                                <CurrencyInputField
                                    label="Valor Total Pago (R$)"
                                    layout="squired"
                                    value={totalCost}
                                    onChange={(val) => setTotalCost(handleCurrencyChange(val))}
                                />
                            </Column>
                            <Column style={{ flex: 1 }}>
                                <Fieldset
                                    label="Fornecedor"
                                    layout="squired"
                                    placeholder="Nome do Fornecedor"
                                    value={supplier}
                                    onChangeValue={setSupplier}
                                />
                            </Column>
                        </ContentBasicFields>
                    )}

                    <ContentBasicFields>
                        <Column style={{ flex: 1 }}>
                            <Fieldset
                                label="Observação"
                                layout="squired"
                                placeholder="Detalhes adicionais..."
                                value={note}
                                onChangeValue={setNote}
                            />
                        </Column>
                    </ContentBasicFields>

                    <ContentButtons>
                        {loading ? (
                            <Loader />
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    variant="light"
                                    category="roundedBorded"
                                    onClick={onClose}
                                >
                                    Cancelar
                                </Button>
                                <Button type="submit">
                                    Confirmar Movimentação
                                </Button>
                            </>
                        )}
                    </ContentButtons>
                </FormContainer>
            </Container>
        </Modal>
    );
}
